<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.card_collections', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div>
            <details class="mt-3 sm:col-span-12">
                <summary>
                    <IconHelpCircle class="h-6 w-6 text-gray-400" />
                </summary>
                <ul class="text-gray-600">
                    <li>
                        <CardRarityIndicator rarity="common" class="inline-block" />
                        {{ $t('messages.common') }}
                    </li>
                    <li>
                        <CardRarityIndicator rarity="uncommon" class="inline-block" />
                        {{ $t('messages.uncommon') }}
                    </li>
                    <li>
                        <CardRarityIndicator rarity="rare" class="inline-block" />
                        {{ $t('messages.rare') }}
                    </li>
                </ul>
            </details>
        </div>

        <EmptyState picture v-if="!meta.has_data">
            <template v-slot:picture />
        </EmptyState>

        <div class="lg:grid lg:grid-cols-2 md:gap-6 bg-gray-50 p-2 mt-3" v-else>
            <AppCard
                class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200"
                v-for="card_collection in card_collections.data"
                :key="card_collection.id"
            >
                <div class="flex space-x-2">
                    <div
                        class="w-10 h-10 bg-gray-300 rounded-full shrink-0 relative bg-no-repeat bg-center bg-cover"
                        :style="card_collection.level ? `background-image: url(${card_collection.image_path});` : ''"
                    >
                        <div
                            v-if="card_collection.level"
                            class="absolute right-0 bottom-0 rounded-full bg-green-100 text-green-800 h-4 w-4 inline-flex items-center justify-center text-sm"
                        >
                            {{ card_collection.level }}
                        </div>
                    </div>
                    <div v-if="card_collection.is_finished" class="mt-1">
                        <AppButton element="button" @click="completeCollection(card_collection)" desired-action>
                            {{ $t('messages.upgrade_card_collection') }}
                        </AppButton>
                    </div>
                </div>
                <div class="w-full flex items-center justify-between p-6 space-x-6">
                    <div class="flex-1 truncate">
                        <div class="flex items-center space-x-3 justify-between">
                            <h3
                                class="text-gray-900 text-sm font-medium truncate"
                                :class="card_collection.is_finished ? 'text-green-600' : ''"
                            >
                                {{ card_collection.name }}
                            </h3>
                            <div>
                                {{ card_collection.cards.filter((c) => c.acquired_by_user).length }}
                                / {{ card_collection.cards.length }}
                            </div>
                        </div>

                        <details class="mt-3" v-if="card_collection.description">
                            <summary>
                                <IconInfoCircle class="h-6 w-6 text-gray-400" />
                            </summary>
                            <p class="mt-1 w-full whitespace-pre-line">
                                {{ card_collection.description }}
                            </p>
                        </details>
                        <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-6 mt-1">
                            <div v-for="card in card_collection.cards" class="" :title="card.name" :key="card.id">
                                <div
                                    class="w-full relative aspect-w-1 aspect-h-1"
                                    :class="{
                                        'border-green-100 border-4': card.acquired_by_user,
                                    }"
                                >
                                    <ZoomableImage :src="card.image_path">
                                        <img
                                            class="w-full h-full object-contain"
                                            :class="{
                                                'filter saturate-0': !card.acquired_by_user,
                                            }"
                                            :src="card.image_path"
                                            alt=""
                                        />
                                        <template #under-image>
                                            <AppCard v-if="card.description">
                                                <p class="whitespace-pre-line">{{ card.description }}</p>
                                            </AppCard>
                                        </template>
                                    </ZoomableImage>
                                </div>
                                <div class="flex items-center justify-between mt-1">
                                    <CardRarityIndicator :rarity="card.rarity" />
                                    <BadgePill
                                        color="indigo"
                                        :class="card.owned_unsealed_by_user ? 'visible' : 'invisible'"
                                    >
                                        &times;{{ card.owned_unsealed_by_user }}
                                    </BadgePill>
                                </div>
                                <div class="flex justify-between items-center mt-1" v-if="card.extra_actions.count">
                                    <button
                                        @click="card.panel_open = true"
                                        type="button"
                                        class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        <svg
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="w-4 h-4"
                                        >
                                            <path
                                                d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"
                                            ></path>
                                        </svg>
                                    </button>
                                    <SlideOver
                                        :show="card.panel_open"
                                        @close="card.panel_open = false"
                                        as="button"
                                        :title="card.name"
                                    >
                                        <div
                                            class="w-full relative aspect-w-1 aspect-h-1"
                                            :class="{
                                                'border-green-100 border-4': card.acquired_by_user,
                                            }"
                                        >
                                            <div
                                                class="w-full h-full absolute bg-no-repeat bg-center bg-contain"
                                                :class="{
                                                    'filter saturate-0': !card.acquired_by_user,
                                                }"
                                                :style="`background-image: url(${card.image_path});`"
                                            />
                                        </div>

                                        <SellSlideOverWidget
                                            v-if="card.extra_actions.actions.sell_back"
                                            :action="card.extra_actions.actions.sell_back"
                                            class="mt-3"
                                        />
                                    </SlideOver>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AppCard>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="card_collections" v-if="meta.has_data" />
        </template>

        <Modal :show="openModal" @close="openModal = false">
            <div class="mt-1" v-if="completedCollection">
                <div class="flex justify-center">
                    <h2 class="inline-block text-3xl">
                        {{ $t('messages.congratulations!') }}
                    </h2>
                </div>
                <div class="flex justify-center mt-5">
                    <div>
                        <div class="md:grid md:grid-cols-3 md:gap-6">
                            <div class="flex justify-center">
                                <div class="">
                                    <img
                                        :src="completedCollection.image_path"
                                        alt=""
                                        class="rounded-full w-full max-w-max max-h-max"
                                    />
                                </div>
                            </div>

                            <div class="sm:col-span-2 flex flex-col justify-center items-center">
                                <div>
                                    {{
                                        $t('messages.you_completed_level_X_of_collection_Y', {
                                            level: completedCollection.level,
                                            collection: completedCollection.name,
                                        })
                                    }}
                                </div>
                                <div class="text-sm text-gray-600 mt-3">
                                    {{ $t('messages.you_have_received_points_for_completing_a_collection') }}
                                </div>
                                <div class="flex space-x-1">
                                    <div>
                                        <BadgePill color="green" v-if="completedCollection.currencyPoint.points">
                                            {{ completedCollection.currencyPoint.points }}
                                        </BadgePill>
                                    </div>

                                    <div>
                                        <BadgePill color="yellow" v-if="completedCollection.currencyPoint.coins_points">
                                            {{ completedCollection.currencyPoint.coins_points }}
                                        </BadgePill>
                                    </div>

                                    <div>
                                        <BadgePill color="red" v-if="completedCollection.currencyPoint.health_points">
                                            {{ completedCollection.currencyPoint.health_points }}
                                        </BadgePill>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import { Howl } from 'howler';
import Modal from '@/components/Modal.vue';
import CardRarityIndicator from '@/components/CardRarityIndicator.vue';
import SlideOver from '@/components/SlideOver.vue';
import SellSlideOverWidget from '@/components/SellSlideOverWidget.vue';
import AppCard from '@/Shared/AppCard.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import { IconHelpCircle, IconInfoCircle } from '@tabler/icons-vue';

export default {
    components: {
        IconHelpCircle,
        IconInfoCircle,
        ZoomableImage,
        AppCard,
        EmptyState,
        PanelPage,
        InertiaPagination,
        AppButton,
        BadgePill,
        Modal,
        CardRarityIndicator,
        SlideOver,
        SellSlideOverWidget,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'card_collections', 'filters', 'completedCollection'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
            openModal: false,
        };
    },
    methods: {
        completeCollection(card_collection) {
            this.$inertia.patch(
                card_collection.links.upgrade_card_collection,
                {},
                {
                    onSuccess: () => {
                        this.showModal();
                    },
                },
            );
        },
        showModal() {
            this.openModal = true;

            setTimeout(() => {
                const sound = new Howl({
                    src: ['/assets/gamification/open_chest.mp3'],
                });
                sound.play();
            }, 1000);
        },
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
    watch: {
        completedCollection: function (completedCollection) {
            if (completedCollection) {
                this.showModal();
            }
        },
    },
};
</script>
<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
